import type { LessonCreated, LessonStorePayload } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface LessonStoreApiOptions {
  disciplineId: string | number
  payload: LessonStorePayload
  onStart?: () => void
  onFinish?: () => void
}

/** `POST /admin-api/disciplines/{discipline}/lessons` — NOT guarded against a duplicate: the caller keeps the submit
 *  disabled while it is pending. A refusal (no balance, tutor busy, no customer timezone) is shown as the API words it. */
export const useLessonStoreApi = async (options: LessonStoreApiOptions): Promise<LessonCreated | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.lessons.api.index', { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)
    const lesson: LessonCreated = data.data

    toast.success({
      title: 'Successfully added!',
      description: 'New lesson was successfully added!',
    })

    return lesson
  } catch (err: unknown) {
    toast.error({
      title: 'Error on create!',
      description: getAxiosErrorMessage(err),
    })

    return null
  } finally {
    options.onFinish?.()
  }
}
